
import React, { useState, useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { createPageUrl } from "../utils/index";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useUser } from "@/hooks/useUser";
import { useClerk } from "@/hooks/useClerk";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useCompany } from "../contexts/CompanyContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import NotificationCenter from "../components/NotificationCenter";
import ErrorBoundary from "../components/ErrorBoundary";
import OnboardingFlow from "../components/OnboardingFlow";
// import { useTheme } from "../hooks/useTheme"; // Removed - dark mode only
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  MapPin,
  Activity,
  Settings,
  CreditCard,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Building2,
  CheckCircle2,
  TrendingUp,
  PhoneCall,
  DollarSign,
  Lock,
  Layers,
  Crown,
  FileText,
  Receipt,
  RefreshCw,
  PiggyBank,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// minLevel: minimum subscription level required to access the page
const navCategories = [
  {
    titleKey: "app.dashboard.title",
    icon: LayoutDashboard,
    items: [
      { nameKey: "app.dashboard.title", icon: LayoutDashboard, page: "Dashboard" },
      { nameKey: "app.sales_dashboard.title", icon: TrendingUp, page: "SalesDashboard", minLevel: 2 },
    ]
  },
  {
    titleKey: "app.clients.title",
    icon: Users,
    items: [
      { nameKey: "app.clients.title", icon: Users, page: "Clients" },
      { nameKey: "app.pipeline.title", icon: FolderKanban, page: "Pipeline" },
      { nameKey: "app.cold_calls.title", icon: PhoneCall, page: "ColdCalls" },
      { nameKey: "app.map.title", icon: MapPin, page: "MapExplorer", minLevel: 2 },
    ]
  },
  {
    titleKey: "app.projects.title",
    icon: FolderKanban,
    items: [
      { nameKey: "app.projects.title", icon: Activity, page: "Projects" },
      { nameKey: "app.tasks.title", icon: CheckCircle2, page: "Tasks" },
      { nameKey: "app.calendar.title", icon: CalendarIcon, page: "Calendar" },
    ]
  },
  {
    titleKey: "app.company.title",
    icon: Building2,
    items: [
      { nameKey: "app.company.work_groups", icon: Layers, page: "WorkGroups", minLevel: 3 },
    ]
  },
  {
    titleKey: "app.finance.title",
    icon: DollarSign,
    items: [
      { nameKey: "app.finance.title", icon: DollarSign, page: "Finance", minLevel: 3 },
      { nameKey: "app.founder_earnings.title", icon: PiggyBank, page: "FounderEarnings", minRank: 5, minLevel: 3 },
      { nameKey: "app.monthly_report.title", icon: FileText, page: "MonthlyReport", minLevel: 2 },
      { nameKey: "app.invoice_request.title", icon: Receipt, page: "InvoiceRequest", minLevel: 2 },
    ]
  },
  {
    titleKey: "app.settings.title",
    icon: Settings,
    items: [
      { nameKey: "app.settings.title", icon: Settings, page: "Settings" },
    ]
  },
  {
    titleKey: "app.admin.title",
    icon: Crown,
    adminOnly: true,
    items: [
      { nameKey: "app.admin.title", icon: Crown, page: "Admin", adminOnly: true },
    ]
  }
];

function CurrencySelector() {
  const { t } = useTranslation();
  const { currency, setCurrency, supportedCurrencies, exchangeRates, loading, formatAmount } = useCurrency();
  const { selectedCompanyId, activeCompanyId } = useCompany();

  const getMetricsArgs = () => {
    // Priority: activeCompanyId > selectedCompanyId > aggregate_all
    if (activeCompanyId) {
      return { company_account_id: activeCompanyId };
    }
    if (selectedCompanyId && selectedCompanyId !== 'personal' && !selectedCompanyId.startsWith('wg_')) {
      return { company_account_id: selectedCompanyId };
    }
    // Default: get everything for the user's company
    return { aggregate_all: true };
  };
  
  const metricsArgs = getMetricsArgs();
  const metrics = useQuery(api.metrics.getPipelineMetrics, metricsArgs);
  const commissions = metrics?.commissions || { totalEarned: 0, totalPending: 0, totalPaid: 0, rate: 0 };

  return (
    <div className="mt-2 space-y-2">
      {/* Balance Summary - always render */}
      {(commissions) && (
        <div className="bg-gray-100 dark:bg-dark-el rounded-lg p-2.5 border border-gray-200 dark:border-dark-line">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 font-medium">{t('app.finance.total_revenue')}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-400">{t('app.finance.income')}</span>
              <span className="text-xs text-green-400 font-semibold">{formatAmount(commissions?.totalEarned || 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-400">{t('common.pending')}</span>
              <span className="text-xs text-amber-400 font-semibold">{formatAmount(commissions?.totalPending || 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-gray-400">{t('app.finance.paid')}</span>
              <span className="text-xs text-white font-semibold">{formatAmount(commissions?.totalPaid || 0)}</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-gray-200 dark:border-dark-line">
              <span className="text-[10px] text-gray-400">{t('app.commissions.rate')}</span>
              <span className="text-xs text-cyan-400 font-semibold">{((commissions?.rate || 0) * 100).toFixed(0)}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Currency Select */}
      <div className="space-y-1.5">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">{t('app.settings.currency')}</p>
        <Select value={currency.code} onValueChange={setCurrency}>
          <SelectTrigger className="w-full h-8 bg-white dark:bg-dark-el border-gray-200 dark:border-dark-line text-xs text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-line transition-colors">
            <SelectValue placeholder={t('app.settings.select_currency')}>
              <span className="flex items-center gap-1.5">
                <span className="font-medium">{currency.symbol}</span>
                <span>{currency.code}</span>
                <span className="text-gray-500">- {currency.name}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-dark-el border-gray-200 dark:border-dark-line text-gray-900 dark:text-white max-h-56">
            {supportedCurrencies.map((c) => {
              const rate = exchangeRates[c.code];
              const isActive = c.code === currency.code;
              return (
                <SelectItem
                  key={c.code}
                  value={c.code}
                  className={`text-xs ${isActive ? 'text-red-400 font-medium bg-red-500/5' : 'text-gray-300'} focus:bg-dark-line focus:text-white`}
                >
                  <span className="flex items-center gap-2 w-full">
                    <span className="font-medium w-4">{c.symbol}</span>
                    <span className="w-8">{c.code}</span>
                    <span className="text-gray-500 flex-1">{c.name}</span>
                    {rate && (
                      <span className="text-[10px] text-gray-500 font-mono">{rate.toFixed(4)}</span>
                    )}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {loading && (
          <p className="text-[10px] text-gray-500 flex items-center gap-1">
            <RefreshCw size={10} className="animate-spin" />
            Updating rates...
          </p>
        )}
      </div>
    </div>
  );
}

export default function Layout({ children, currentPageName }) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useUser();
  const { signOut } = useClerk();
  const { mustChangePassword } = useAuth();

  // Collapsed state — persisted in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('crm_sidebar_collapsed') === 'true';
  });

  const toggleCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('crm_sidebar_collapsed', String(next));
      return next;
    });
  };

  const [openCategories, setOpenCategories] = useState(() => {
    const saved = localStorage.getItem('crm_sidebar_categories');
    return saved ? JSON.parse(saved) : {};
  });

  const toggleCategory = (title) => {
    if (sidebarCollapsed) return; // don't toggle categories in icon mode
    const newOpenCategories = { ...openCategories, [title]: openCategories[title] === false ? true : false };
    setOpenCategories(newOpenCategories);
    localStorage.setItem('crm_sidebar_categories', JSON.stringify(newOpenCategories));
  };

  // Use CompanyContext for role-based access FIRST (before any hooks that depend on it)
  const companyCtx = useCompany();
  const { userRole, userRank, isAdmin, canUserAccessPage } = companyCtx;

    const companies = useQuery(api.company.getAll) || [];

    const { isLoaded, isSignedIn } = useUser();

    // Don't render layout for public pages
    const publicPages = ['Landing', 'DiscoverCRM'];
    if (publicPages.includes(currentPageName)) {
        return children;
    }

    // Force-change-password gate (only on protected pages)
    if (mustChangePassword && currentPageName !== 'ChangePassword') {
        return <Navigate to="/change-password" replace />;
    }

    if (!isLoaded) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-dark-bg">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
            </div>
        );
    }

  const handleLogout = async () => {
    await signOut();
  };

  // Sidebar width classes
  const sidebarW = sidebarCollapsed ? 'w-16' : 'w-64';
  const mainML = sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64';

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-dark-bg text-gray-900 dark:text-white">
      <style>{`
        :root {
          --core-red: #dc2626;
          --core-red-light: #ef4444;
          --core-red-dark: #b91c1c;
          --matrix-green: #22c55e;
        }
        .dark {
          --bg-primary: #0a0a0a;
          --bg-secondary: #111111;
          --bg-tertiary: #1a1a1a;
          --border-color: #262626;
        }
        :not(.dark) {
          --bg-primary: #ffffff;
          --bg-secondary: #f9fafb;
          --bg-tertiary: #f3f4f6;
          --border-color: #e5e7eb;
        }

        .matrix-glow {
          box-shadow: 0 0 20px rgba(220, 38, 38, 0.15);
        }

        .red-gradient {
          background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
        }

        .sidebar-item:hover {
          background: linear-gradient(90deg, rgba(220, 38, 38, 0.08) 0%, transparent 100%);
        }

        .sidebar-item.active {
          background: linear-gradient(90deg, rgba(220, 38, 38, 0.18) 0%, transparent 100%);
        }

        /* In expanded mode, add left indicator only on the padding side */
        .sidebar-nav-expanded .sidebar-item:hover {
          border-left: 2px solid rgba(220, 38, 38, 0.6);
        }
        .sidebar-nav-expanded .sidebar-item.active {
          border-left: 2px solid #dc2626;
        }

        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #dc2626;
          border-radius: 10px;
        }

        /* Tooltip for collapsed sidebar icons */
        .sidebar-tooltip {
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease;
        }
        .sidebar-tooltip-trigger:hover .sidebar-tooltip {
          visibility: visible;
          opacity: 1;
        }
      `}</style>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-line px-3 py-2.5 flex items-center justify-between safe-area-top">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2.5 hover:bg-gray-200 dark:hover:bg-dark-el rounded-xl active:bg-gray-300 dark:active:bg-dark-line transition-colors shrink-0 text-gray-700 dark:text-white"
            aria-label={sidebarOpen ? t('common.close_menu') : t('common.open_menu')}
          >
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <a href="https://core829.net" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 min-w-0">
            <img
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/693abcae586ea1ac8122ea70/21aa16bfb_CORE829LARGEPXlogonolettersnobg.png"
              alt="Core829"
              className="h-7 w-auto shrink-0 core829-logo"
            />
            <span className="font-semibold text-sm truncate hidden xs:inline text-gray-900 dark:text-white">Core829</span>
          </a>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && (
            <Link
              to={createPageUrl("Admin")}
              className="p-2.5 hover:bg-dark-el rounded-xl active:bg-dark-line transition-colors text-amber-400"
              aria-label={t('app.admin.title')}
            >
              <Crown size={20} />
            </Link>
          )}
          {user && <NotificationCenter userEmail={user.primaryEmailAddress?.emailAddress} />}
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-16 left-0 bottom-20 lg:top-0 lg:bottom-0 w-64 bg-gray-100 dark:bg-dark-card border-r border-gray-300 dark:border-dark-line z-40
        transform transition-all duration-250 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">

          {/* Logo + Collapse Toggle */}
          <div className={`border-b border-gray-200 dark:border-dark-line flex items-center shrink-0 ${sidebarCollapsed ? 'justify-center p-3' : 'justify-between p-4'}`}>
            {!sidebarCollapsed && (
              <a href="https://core829.net" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/693abcae586ea1ac8122ea70/21aa16bfb_CORE829LARGEPXlogonolettersnobg.png"
                  alt="Core829"
                  className="w-9 h-9 object-contain core829-logo"
                />
                <div>
                  <h1 className="font-bold text-base leading-none text-gray-900 dark:text-white">Core829</h1>
                  <p className="text-[10px] text-gray-500 mt-0.5">{t('landing.hero.headline')}</p>
                </div>
              </a>
            )}
            {sidebarCollapsed && (
              <a href="https://core829.net" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/693abcae586ea1ac8122ea70/21aa16bfb_CORE829LARGEPXlogonolettersnobg.png"
                  alt="Core829"
                  className="w-8 h-8 object-contain core829-logo"
                />
              </a>
            )}
            {/* Collapse button — desktop only */}
            <button
              onClick={toggleCollapsed}
              className={`hidden lg:flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-100 dark:hover:bg-dark-line text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors shrink-0`}
              title={sidebarCollapsed ? 'Espandi menu' : 'Comprimi menu'}
            >
              {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-3 overflow-y-auto scrollbar-thin">
            <div className={`space-y-0.5 ${sidebarCollapsed ? 'px-1' : 'px-3 sidebar-nav-expanded'}`}>
              {navCategories.map((category) => {
                if (category.adminOnly && !isAdmin) return null;

                const isCategoryOpen = openCategories[category.titleKey] !== false;

                const visibleItems = category.items.filter(i => {
                    if (i.adminOnly && !isAdmin) return false;
                    // Role-based page access filtering
                    if (!isAdmin && i.page && !canUserAccessPage(i.page)) return false;
                    return true;
                });

                if (visibleItems.length === 0) return null;

                const hasActiveChild = visibleItems.some(i => i.page === currentPageName);

                // In collapsed mode: show only icons, no category headers
                if (sidebarCollapsed) {
                  return (
                    <div key={category.titleKey} className="py-1 border-b border-dark-el last:border-0">
                      {visibleItems.map((item) => {
                        const isActive = currentPageName === item.page;

                        let isLocked = false;
                        let lockReason = "";

                        if (!isAdmin) {
                          if (item.minRank && userRank < item.minRank) { isLocked = true; lockReason = t('common.insufficient_role'); }
                          else if (item.page && !canUserAccessPage(item.page)) { isLocked = true; lockReason = t('common.insufficient_role'); }
                        }

                        return (
                          <div key={item.nameKey} className="relative sidebar-tooltip-trigger">
                            <Link
                              to={isLocked ? createPageUrl("Dashboard") : createPageUrl(item.page)}
                              onClick={() => setSidebarOpen(false)}
                              title={t(item.nameKey)}
                              className={`
                                sidebar-item flex items-center justify-center w-full py-2.5 rounded-lg transition-all duration-150
                                ${isActive ? 'active text-red-500 bg-red-50 dark:bg-red-500/10' : isLocked ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-dark-el'}
                              `}
                            >
                              <item.icon size={18} className={isLocked ? 'opacity-40' : ''} />
                            </Link>
                              <div className="sidebar-tooltip absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-dark-el borderborder-dark-line text-white text-xs px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-lg pointer-events-none">
                              {t(item.nameKey)}
                              {isLocked && <span className="ml-1 text-gray-500">· {lockReason}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Expanded mode: full labels + category headers
                return (
                  <div key={category.titleKey} className="mb-2">
                    <button
                      onClick={() => toggleCategory(category.titleKey)}
                      className={`w-full flex items-center justify-between px-2 py-1.5 text-sm transition-colors rounded-md
                        ${hasActiveChild ? 'text-red-500' : 'text-gray-400 hover:text-white'}
                      `}
                    >
                      <div className="flex items-center gap-2 uppercase tracking-wider text-[10px] font-semibold">
                        <category.icon size={12} />
                        {t(category.titleKey)}
                      </div>
                      <ChevronDown
                        size={12}
                        className={`transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    <div className={`space-y-0.5 overflow-hidden transition-all duration-300 ${isCategoryOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {visibleItems.map((item) => {
                        const isActive = currentPageName === item.page;

                        let isLocked = false;
                        let lockReason = "";

                        if (!isAdmin) {
                          if (item.minRank && userRank < item.minRank) { isLocked = true; lockReason = t('app.errors.unauthorized'); }
                          else if (item.page && !canUserAccessPage(item.page)) { isLocked = true; lockReason = t('app.errors.unauthorized'); }
                          // company membership no longer gated
                        }

                        if (isLocked) {
                          return (
                            <Link
                              key={item.nameKey}
                              to={createPageUrl("Dashboard")}
                              onClick={() => setSidebarOpen(false)}
                              title={lockReason}
                              className="sidebar-item flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 ml-1 text-gray-500 dark:text-gray-600 hover:bg-gray-200 dark:hover:bg-dark-el group"
                            >
                              <item.icon size={16} className="opacity-40 shrink-0" />
                              <span className="text-sm font-medium flex-1 opacity-50 truncate">{t(item.nameKey)}</span>
                              <Lock size={10} className="text-gray-600 shrink-0" />
                            </Link>
                          );
                        }

                        return (
                          <Link
                            key={item.nameKey}
                            to={createPageUrl(item.page)}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              sidebar-item flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 ml-1
                              ${isActive
                                ? 'active text-red-500 bg-red-500/10'
                                : 'text-gray-400 hover:text-white hover:bg-dark-el'}
                            `}
                          >
                            <item.icon size={16} className="shrink-0" />
                            <span className="text-sm font-medium truncate">{t(item.nameKey)}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Currency & Balance */}
          {user && !sidebarCollapsed && (
            <div className="px-3 py-2 border-tborder-dark-line">
              <CurrencySelector />
            </div>
          )}

          {/* User Section */}
          {user && (
            <div className={`border-tborder-dark-line ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
              {!sidebarCollapsed && (
                <div className="flex justify-end mb-2">
                  <NotificationCenter userEmail={user.primaryEmailAddress?.emailAddress} />
                </div>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={`w-full flex items-center gap-2 p-2 rounded-lg bg-gray-200 dark:bg-dark-el hover:bg-gray-300 dark:hover:bg-dark-line transition-colors text-gray-700 dark:text-gray-300 ${sidebarCollapsed ? 'justify-center' : ''}`}>
                    {user.imageUrl ? (
                      <img src={user.imageUrl} alt="" className="w-7 h-7 rounded-full object-cover border border-red-500/30 shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full red-gradient flex items-center justify-center text-xs font-bold shrink-0">
                        {user.fullName?.[0] || user.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase()}
                      </div>
                    )}
                    {!sidebarCollapsed && (
                      <>
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-xs font-medium truncate">{user.fullName || t('common.user')}</p>
                          <p className="text-[10px] text-gray-500 truncate">{user.primaryEmailAddress?.emailAddress}</p>
                        </div>
                        <ChevronDown size={14} className="text-gray-500 shrink-0" />
                      </>
                    )}
                  </button>
                </DropdownMenuTrigger>
<DropdownMenuContent className="w-52 bg-white dark:bg-dark-el border border-gray-200 dark:border-dark-line" align={sidebarCollapsed ? "center" : "end"} side="top">
                  {sidebarCollapsed && (
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-dark-line">
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{user.fullName || t('common.user')}</p>
                      <p className="text-[10px] text-gray-500 truncate">{user.primaryEmailAddress?.emailAddress}</p>
                    </div>
                  )}
                  <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-line">
                    <Link to={createPageUrl("Settings")} className="flex items-center gap-2 w-full">
                      <Settings size={15} />
                      {t('app.settings.title')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-gray-200 dark:bg-dark-line" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 dark:text-red-500 hover:bg-gray-100 dark:hover:bg-dark-line cursor-pointer"
                  >
                    <LogOut size={15} className="mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content — adapts to sidebar width */}
      <main className={`${mainML} min-h-screen pt-16 lg:pt-0 pb-20 lg:pb-0 transition-all duration-250 ease-in-out`}>
        <div className="p-4 lg:p-8">
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gray-100 dark:bg-dark-card border-t border-gray-300 dark:border-dark-line safe-area-bottom">
        <div className="flex items-center justify-around py-1.5 px-1">
          <Link
            to={createPageUrl("Dashboard")}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${currentPageName === 'Dashboard' ? 'text-red-500' : 'text-gray-400'}`}
          >
            <LayoutDashboard size={22} />
            <span className="text-[9px] font-medium">{t('app.dashboard.title')}</span>
          </Link>
          <Link
            to={createPageUrl("Clients")}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${currentPageName === 'Clients' ? 'text-red-500' : 'text-gray-400'}`}
          >
            <Users size={22} />
            <span className="text-[9px] font-medium">{t('app.clients.title')}</span>
          </Link>
          <Link
            to={createPageUrl("Pipeline")}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${currentPageName === 'Pipeline' ? 'text-red-500' : 'text-gray-400'}`}
          >
            <FolderKanban size={22} />
            <span className="text-[9px] font-medium">{t('app.pipeline.title')}</span>
          </Link>
          <Link
            to={createPageUrl("Calendar")}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${currentPageName === 'Calendar' ? 'text-red-500' : 'text-gray-400'}`}
          >
            <CalendarIcon size={22} />
            <span className="text-[9px] font-medium">{t('app.calendar.title')}</span>
          </Link>
          {isAdmin && (
            <Link
              to={createPageUrl("Admin")}
              className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${currentPageName === 'Admin' ? 'text-amber-400' : 'text-gray-400'}`}
            >
              <Crown size={22} />
              <span className="text-[9px] font-medium">{t('app.admin.title_short')}</span>
            </Link>
          )}
          <Link
            to={createPageUrl("Settings")}
            className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl transition-colors min-w-[56px] ${currentPageName === 'Settings' ? 'text-red-500' : 'text-gray-400'}`}
          >
            <Settings size={22} />
            <span className="text-[9px] font-medium">{t('common.menu')}</span>
          </Link>
        </div>
      </nav>

      <OnboardingFlow />
    </div>
  );
}
